import { useEffect, useState } from 'react'
import {
  Sparkles, Map, Bot, Download, Calendar, ArrowRight, ArrowLeft, X, CheckCircle2,
} from 'lucide-react'

const STORAGE_KEY = 'caja-onboarding-tour-v1'

/**
 * 5-step welcome modal shown the first time a Caja user lands on /caja.
 *
 * We deliberately keep this as a self-contained modal (no DOM-spotlight) because:
 *   • Spotlights need exact element coordinates and break when the layout shifts.
 *   • A modal with concrete next-actions converts better than a passive tooltip tour.
 *
 * The flag lives in localStorage so it persists across reloads but doesn't follow
 * the user across devices — that's intentional. We *want* repeat exposure if they
 * change browser/laptop because the dashboard takes a beat to grok.
 *
 * Re-opening: `<OnboardingTour onClose triggerKey={n} />` — bumping triggerKey
 * forces it to show again (used by the help button in the dashboard).
 */
export default function OnboardingTour({ companyName, nextPhaseName, onClose, triggerKey = 0 }) {
  const [step, setStep] = useState(0)
  const [open, setOpen] = useState(false)

  // Show on first visit, or when triggerKey changes (manual re-open)
  useEffect(() => {
    if (triggerKey > 0) { setOpen(true); setStep(0); return }
    try {
      const seen = localStorage.getItem(STORAGE_KEY)
      if (!seen) setOpen(true)
    } catch {/* private mode / storage disabled */}
  }, [triggerKey])

  function dismiss() {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())) } catch {/* ignore */}
    setOpen(false)
    onClose?.()
  }

  if (!open) return null

  const steps = [
    {
      icon: Sparkles,
      title: `¡Bienvenido/a${companyName ? `, ${companyName}` : ''}!`,
      body: 'Vamos a construir un plan estratégico profesional siguiendo la metodología de Next Valley Consultants. Te tomará ~10 minutos por sección y te guiamos paso a paso.',
      cta: 'Empezar el tour',
    },
    {
      icon: Map,
      title: 'Las 7 etapas de tu plan',
      body: 'Verás un roadmap con 7 etapas, desde "Modelo de Negocio" hasta "Plan Corporativo". Cada etapa tiene varias secciones — completa una a la vez, no tienes que hacerlas en un solo día.',
      cta: 'Siguiente',
    },
    {
      icon: Bot,
      title: 'Tu agente de IA siempre contigo',
      body: nextPhaseName
        ? `Empieza por "${nextPhaseName}". Adentro de cada sección hay una guía con preguntas clave y un asistente de IA que conoce tu proyecto y responde dudas estratégicas.`
        : 'Adentro de cada sección hay una guía con preguntas clave y un asistente de IA que conoce tu proyecto y responde dudas estratégicas.',
      cta: 'Siguiente',
    },
    {
      icon: Download,
      title: 'Descarga tu plan en cualquier momento',
      body: 'Cuando quieras (incluso a medio camino), abajo en este dashboard tienes un botón para descargar tu plan en Word o PDF — versión completa o resumen ejecutivo para presentar a la junta.',
      cta: 'Siguiente',
    },
    {
      icon: Calendar,
      title: 'Sesiones con consultor cuando las necesites',
      body: '¿Tienes una decisión estratégica difícil? Compra un pack de sesiones 1 a 1 con un consultor NVC — abajo en el dashboard. Las sesiones nunca caducan, las usas cuando quieras.',
      cta: '¡Listo, empezar!',
    },
  ]

  const current = steps[step]
  const Icon = current.icon
  const isLast = step === steps.length - 1
  const isFirst = step === 0

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">

        {/* Progress dots */}
        <div className="flex items-center justify-between px-6 pt-5">
          <div className="flex items-center gap-1.5">
            {steps.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setStep(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? 'w-6 bg-brand-700' : i < step ? 'w-1.5 bg-brand-300' : 'w-1.5 bg-gray-200'
                }`}
                aria-label={`Ir al paso ${i + 1}`}
              />
            ))}
          </div>
          <button
            onClick={dismiss}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Cerrar tour"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="px-7 py-6 text-center">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-brand-50 to-accent-50 flex items-center justify-center mx-auto mb-4">
            <Icon size={26} className="text-brand-700" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{current.title}</h2>
          <p className="text-sm text-gray-500 leading-relaxed">{current.body}</p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 px-6 pb-6">
          {!isFirst && (
            <button
              onClick={() => setStep(step - 1)}
              className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 px-3 py-2 transition-colors"
            >
              <ArrowLeft size={14} /> Atrás
            </button>
          )}
          <div className="flex-1" />
          {!isLast && (
            <button
              onClick={dismiss}
              className="text-xs text-gray-400 hover:text-gray-600 px-3 py-2 transition-colors"
            >
              Saltar
            </button>
          )}
          <button
            onClick={() => isLast ? dismiss() : setStep(step + 1)}
            className="flex items-center gap-1.5 bg-brand-700 hover:bg-brand-800 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"
          >
            {isLast
              ? <><CheckCircle2 size={14} /> {current.cta}</>
              : <>{current.cta} <ArrowRight size={14} /></>
            }
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Helper hook to programmatically re-open the tour.
 * Returns [triggerKey, openTour] — pass triggerKey to <OnboardingTour /> and
 * call openTour() from a help button.
 */
export function useTourTrigger() {
  const [triggerKey, setTriggerKey] = useState(0)
  return [triggerKey, () => setTriggerKey(k => k + 1)]
}
